import { useEffect, useState, useMemo } from "react";
import { customerAPI, licenseAPI } from "../services/api";
import FilledButton from "../components/FilledButton";
import DataTable from "../components/DataTable";
import AdvancedSearchBar from "../components/AdvancedSearchBar";
import GenerateLicenseModal from "../components/GenerateLicenseModal";
import RenewLicenseModal from "../components/RenewLicenseModal";
import LicenseKeyModal from "../components/LicenseKeyModal";
import LicenseHistoryModal from "../components/LicenseHistoryModal";
import {
  Add as AddIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import { format, parseISO, isValid } from "date-fns";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showLicenseKeyModal, setShowLicenseKeyModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showLicenseHistoryModal, setShowLicenseHistoryModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [renewingLicense, setRenewingLicense] = useState(null);
  const [generatedLicenseKey, setGeneratedLicenseKey] = useState("");
  const [generatedLicenseId, setGeneratedLicenseId] = useState("");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load customers when debounced search query or page changes
  useEffect(() => {
    loadCustomers();
  }, [debouncedSearchQuery, page]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchQuery]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await customerAPI.getAll(page, pageSize, debouncedSearchQuery);
      setCustomers(data.customers || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      toast.error("Failed to load customers");
      setCustomers([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setShowLicenseHistoryModal(true);
  };

  const handleGenerate = async (formData) => {
    try {
      const processedData = {
        ...formData,
        purchaseAmount: formData.purchaseAmount
          ? parseFloat(formData.purchaseAmount)
          : undefined,
      };
      const response = await licenseAPI.generate(processedData);
      setGeneratedLicenseKey(response.licenseKey);
      setGeneratedLicenseId(response.licenseId);
      setShowGenerateModal(false);
      setShowLicenseKeyModal(true);
      loadCustomers(); // Refresh customer data to update license counts
      toast.success("License generated successfully!");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to generate license"
      );
    }
  };

  const handleRenew = async (license) => {
    setRenewingLicense(license);
    setShowRenewModal(true);
  };

  const handleRenewSubmit = async (formData) => {
    try {
      const response = await licenseAPI.renew(renewingLicense.licenseId, formData);
      setGeneratedLicenseKey(response.licenseKey);
      setGeneratedLicenseId(response.licenseId);
      setShowRenewModal(false);
      setShowLicenseKeyModal(true);
      setRenewingLicense(null);
      loadCustomers(); // Refresh customer data
      toast.success("License renewed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to renew license");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleCloseModals = () => {
    setShowLicenseKeyModal(false);
    setShowLicenseHistoryModal(false);
    setGeneratedLicenseKey("");
    setGeneratedLicenseId("");
    setSelectedCustomer(null);
    setRenewingLicense(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      let date;
      if (typeof dateString === "string") {
        const parsed = parseISO(dateString);
        date = isValid(parsed) ? parsed : new Date(dateString);
      } else {
        date = new Date(dateString);
      }
      if (!isValid(date)) return "—";
      return format(date, "MMM dd, yyyy");
    } catch (error) {
      return "—";
    }
  };

  // Filter customers by status (search is handled by backend)
  const filteredCustomers = useMemo(() => {
    let filtered = [...customers];

    // Status filter (based on license activity)
    if (statusFilter !== "all") {
      if (statusFilter === "active") {
        filtered = filtered.filter((customer) => customer.activeLicenseCount > 0);
      } else if (statusFilter === "inactive") {
        filtered = filtered.filter((customer) => customer.activeLicenseCount === 0);
      }
    }

    // Sort (since backend doesn't handle sorting yet)
    filtered.sort((a, b) => {
      let aValue, bValue;

      if (sortBy === "createdAt") {
        aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      } else if (sortBy === "lastLicenseDate") {
        aValue = a.lastLicenseDate ? new Date(a.lastLicenseDate).getTime() : 0;
        bValue = b.lastLicenseDate ? new Date(b.lastLicenseDate).getTime() : 0;
      } else if (sortBy === "licenseCount") {
        aValue = a.licenseCount || 0;
        bValue = b.licenseCount || 0;
      } else {
        return 0;
      }

      if (sortOrder === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return filtered;
  }, [customers, statusFilter, sortBy, sortOrder]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const columns = [
    {
      key: "name",
      label: "Customer Name",
      path: "name",
    },
    {
      key: "email",
      label: "Email",
      path: "email",
    },
    {
      key: "licenseCount",
      label: "Total Licenses",
      path: "licenseCount",
    },
    {
      key: "activeLicenseCount",
      label: "Active Licenses",
      path: "activeLicenseCount",
    },
    {
      key: "pendingLicenseCount",
      label: "Pending Licenses",
      path: "pendingLicenseCount",
    },
    {
      key: "revokedLicenseCount",
      label: "Revoked Licenses",
      path: "revokedLicenseCount",
    },
    {
      key: "createdAt",
      label: "Customer Since",
      path: "createdAt",
    },
    {
      key: "lastLicenseDate",
      label: "Last License",
      path: "lastLicenseDate",
    },
  ];

  const customRenderers = {
    name: (value, item) => (
      <div className="flex items-center gap-2">
        <PersonIcon className="w-5 h-5 text-gray-400" />
        <div>
          <div className="text-sm font-medium text-gray-900">
            {value || "—"}
          </div>
        </div>
      </div>
    ),
    email: (value) => (
      <div className="text-sm text-gray-700">{value || "—"}</div>
    ),
    licenseCount: (value) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {value || 0}
      </span>
    ),
    activeLicenseCount: (value) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {value || 0}
      </span>
    ),
    pendingLicenseCount: (value) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {value || 0}
      </span>
    ),
    revokedLicenseCount: (value) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {value || 0}
      </span>
    ),
    createdAt: (value) => (
      <div className="text-sm font-medium text-gray-900">
        {formatDate(value)}
      </div>
    ),
    lastLicenseDate: (value) => (
      <div className="text-sm font-medium text-gray-900">
        {formatDate(value)}
      </div>
    ),
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Customers</h1>
          <p className="text-slate-600 text-sm mt-1">
            {totalCount} customer{totalCount !== 1 ? "s" : ""} found
            {debouncedSearchQuery && ` for "${debouncedSearchQuery}"`}
          </p>
        </div>
        <FilledButton
          text="Generate License"
          width="w-min"
          IconFirst={AddIcon}
          btnClicked={() => setShowGenerateModal(true)}
          textColor="white"
        />
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar */}
        <AdvancedSearchBar
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          onSearch={(term) => {
            setSearchQuery(term);
          }}
          placeholder="Search by customer name or email..."
          enableDebounce={true}
          debounceDelay={500}
          isLoading={loading}
        />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex flex-wrap gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGreen/20 text-sm"
              >
                <option value="all">All Customers</option>
                <option value="active">Active Customers</option>
                <option value="inactive">Inactive Customers</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={filteredCustomers}
        columns={columns}
        isLoading={loading}
        searchQuery={debouncedSearchQuery}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        itemName="customer"
        isClickable={true}
        onView={handleCustomerClick}
        fieldMappings={{
          dateFields: ["createdAt", "lastLicenseDate"],
          statusFields: [],
          actionFields: [],
        }}
        customRenderers={customRenderers}
      />

      {/* Generate Modal */}
      <GenerateLicenseModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onSubmit={handleGenerate}
      />

      {/* Renew Modal */}
      <RenewLicenseModal
        isOpen={showRenewModal}
        onClose={() => setShowRenewModal(false)}
        onSubmit={handleRenewSubmit}
        license={renewingLicense}
      />

      {/* License Key Modal */}
      <LicenseKeyModal
        isOpen={showLicenseKeyModal}
        onClose={handleCloseModals}
        licenseKey={generatedLicenseKey}
        licenseId={generatedLicenseId}
        onCopyToClipboard={copyToClipboard}
      />

      {/* License History Modal */}
      <LicenseHistoryModal
        isOpen={showLicenseHistoryModal}
        onClose={handleCloseModals}
        customer={selectedCustomer}
        onRenewLicense={handleRenew}
        onRevokeLicense={loadCustomers}
      />
    </div>
  );
}
